<template>
  <div class="w-full min-h-screen flex flex-col">
    <!-- Header with Logo -->
    <HeaderBar :logo="logo">
      <template #account>
        <LoginDropdown />
      </template>
    </HeaderBar>

    <div class="flex-1 w-full">
      <div class="max-w-4xl mx-auto sm:px-6 px-3 sm:py-8 py-4">
        <!-- Loading -->
        <section
          v-if="loading"
          class="bg-white/90 backdrop-blur-md rounded-2xl shadow-xl border border-gray-100 p-12 flex flex-col items-center justify-center gap-4"
        >
          <img class="h-24" :src="logo" alt="Loading" />
          <p class="text-gray-600">Loading investigation...</p>
        </section>

        <!-- Error -->
        <section
          v-else-if="loadError"
          class="bg-white/90 backdrop-blur-md rounded-2xl shadow-xl border border-gray-100 p-12 text-center"
        >
          <p class="mt-4 text-red-600 font-medium">{{ loadError }}</p>
        </section>

        <section v-else class="space-y-6">
          <div
            class="bg-white/90 backdrop-blur-md rounded-2xl shadow-xl border border-gray-100 p-4 sm:p-8"
          >
            <p class="text-xs text-gray-500">Investigation ID: {{ investigationId }}</p>
            <h1 class="text-2xl font-semibold">
              {{ investigationInfo?.title || investigationInfo?.name }}
            </h1>
            <p class="text-sm text-gray-600 mt-1">
              Add a new dataset to this investigation's
              <span class="font-medium">{{ experimentName }}</span> experiment.
            </p>
          </div>

            <!-- File Uploader -->
            <div
              class="bg-gradient-to-br from-spicy-mustard-100 to-mocha-mousse-50 rounded-2xl p-3 md:p-6 shadow-sm"
            >
              <h3 class="font-semibold mb-4 flex items-center gap-2">
                <Icon icon="mdi:cloud-upload" class="w-5 h-5 text-mocha-mousse" />
                Upload Files
              </h3>
              <FileAndFolderLoader ref="FileComp" />

              <p
                v-if="analyzing"
                class="mt-2 text-sm text-gray-600 flex items-center gap-1"
              >
                <Icon icon="eos-icons:loading" class="w-4 h-4 animate-spin" />
                Analyzing files...
              </p>

              <p v-if="hasAnalyzeError" class="mt-2 text-sm text-red-600 flex items-center gap-1">
                <Icon icon="mdi:alert-circle" class="w-4 h-4" />
                {{ analyzeError }}
              </p>
            </div>

            <!-- Description -->
            <div
              v-if="previewReady"
              class="bg-gradient-to-br from-spicy-mustard-100 to-mocha-mousse-50 rounded-2xl p-6 flex flex-col gap-4 shadow-sm"
            >
              <div class="flex items-center gap-2 font-semibold">
                <span>Description</span>
                <div class="relative group inline-block">
                  <Icon
                    icon="mdi:information-outline"
                    class="w-4 h-4 text-gray-400 hover:text-mocha-mousse-600 cursor-help transition-colors"
                  />
                  <div
                    class="absolute left-1/2 -translate-x-1/2 top-6 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 shadow-xl z-50"
                  >
                    Automatically generated from the uploaded files. Review and
                    edit before creating the dataset. Saved as
                    <strong>.description</strong> in the new dataset folder.
                  </div>
                </div>
              </div>

              <div class="relative">
                <textarea
                  rows="10"
                  class="p-3 w-full rounded-xl resize-none text-sm shadow-sm focus:ring-4 focus:ring-blue-100 transition-all bg-white/90 backdrop-blur-md"
                  @keydown="handleKeydown"
                  @input="handleInput"
                  :value="formState.description"
                ></textarea>
                <div
                  class="absolute -bottom-6 right-0 flex items-center text-gray-400 text-xs gap-1"
                >
                  <span>{{ encodedDescriptionLength }}</span>
                  / {{ maxDescriptionLength }}
                </div>
              </div>

              <div class="flex justify-end pt-4">
                <button
                  @click="confirmSubmit"
                  :disabled="!formStore.isDescriptionValid || submitting"
                  class="group relative px-8 py-3 cursor-pointer bg-gradient-to-r from-mint-green-100 to-periwinkle-100 font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-2"
                >
                  <Icon
                    v-if="submitting"
                    icon="eos-icons:loading"
                    class="w-5 h-5 animate-spin"
                  />
                  <span>{{ submitting ? "Creating..." : "Create Dataset" }}</span>
                </button>
              </div>

              <p v-if="submitError" class="mt-2 text-sm text-red-600 flex items-center gap-1">
                <Icon icon="mdi:alert-circle" class="w-4 h-4" />
                {{ submitError }}
              </p>
            </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import apiClient from "@/services/api";
import authService from "@/services/authService";
import { useFormStore } from "@/stores/FormStore";
import { useDescription } from "@/composables/useDescription";
import { usePreviewWorkflow } from "@/composables/usePreviewWorkflow";
import { useModalService } from "@/services/ModalService";
import HeaderBar from "@/components/HeaderBar.vue";
import LoginDropdown from "@/components/LoginDropdown.vue";
import FileAndFolderLoader from "@/components/FileAndFolderLoader.vue";
import { Icon } from "@iconify/vue";

const route = useRoute();
const router = useRouter();
const investigationId = route.params.investigation_id;
const experimentName = "exp0";

const logo = ref(`${process.env.BASE_URL}images/enodo-logo.png`);
const loading = ref(true);
const loadError = ref(null);
const investigationInfo = ref(null);

const formStore = useFormStore();
const formState = formStore.formState;

const FileComp = ref(null);
const previewReady = ref(false);

const submitting = ref(false);
const submitError = ref(null);
const modalSvc = useModalService();

const {
  maxDescriptionLength,
  encodedDescriptionLength,
  createDescription,
  handleInput,
  handleKeydown,
} = useDescription(formState);

const {
  isLoading: analyzing,
  error: hasAnalyzeError,
  error_message: analyzeError,
  startPreviewAndPollStatus,
} = usePreviewWorkflow("add-dataset-preview");

onMounted(async () => {
  formStore.resetAll();

  try {
    const resp = await apiClient.getInvestigationInfoByID(investigationId);
    if (resp.data.result !== "OK") {
      throw new Error(
        resp.data.message?.error?.details || "Investigation not found",
      );
    }
    investigationInfo.value = resp.data.message;

    formStore.patch({
      title: investigationInfo.value.title || investigationInfo.value.name,
      upload_type: "Project",
    });

    const user = authService.currentUser.value;
    if (user && user.vuo_id) {
      formStore.setLeadUploader({
        id: user.vuo_id,
        name: user.name || "",
        lastname: user.lastname || "",
        email: user.vuo_email || user.email || "",
        institute: user.institute || "",
      });
    }
  } catch (e) {
    loadError.value = e?.message || "Failed to load investigation";
  } finally {
    loading.value = false;
  }
});

const analyzeFiles = async () => {
  if (!FileComp.value.validateInput()) return;

  previewReady.value = false;
  formStore.patch({ description: "" });

  const formData = new FormData();
  formData.append("file", formState.file);
  formData.append("file_custom_type", formState.file?.custom_type || "");
  formData.append("filename", formState.filename);

  await startPreviewAndPollStatus(
    formData,
    (message) => {
      formStore.setStructure(message.file_structure || {});
      createDescription({
        n_files: message.n_files,
        extensions: message.extensions,
        dimension: message.dimension,
      });
      previewReady.value = true;
    },
    (fd, onProgress) =>
      apiClient.getAddDatasetPreview(investigationId, fd, onProgress),
  );
};

// Analyze as soon as a file is selected; hide the description when it's removed
watch(
  () => formState.file,
  (file) => {
    if (file) {
      analyzeFiles();
    } else {
      previewReady.value = false;
    }
  },
);

const confirmSubmit = () => {
  modalSvc.show({
    title: "Confirm Submission",
    message:
      "Are you sure you want to continue with the upload? This operation is final.",
    icon: "mdi:alert-circle-outline",
    buttons: [
      { text: "Cancel", variant: "secondary", action: () => {} },
      { text: "Confirm", variant: "primary", action: submitDataset },
    ],
  });
};

const submitDataset = async () => {
  submitting.value = true;
  submitError.value = null;

  try {
    const resp = await apiClient.confirmAddDataset(
      investigationId,
      formState.description,
    );
    if (resp.data.result === "OK") {
      router.push({
        name: "myUploadDataset",
        params: { investigation_id: investigationId },
      });
    } else {
      submitError.value =
        resp.data.message?.error?.details || "Failed to create dataset";
    }
  } catch (e) {
    submitError.value =
      e?.response?.data?.message ?? e?.message ?? "Failed to create dataset";
  } finally {
    submitting.value = false;
  }
};
</script>
